import { useQuery } from '@apollo/client';
import {
  ActionGroup,
  ALL_SHARD_MODULES,
  DEFAULT_CLIENT_FIELDS_BY_COLUMN_NAME,
  getParsedCrudData,
  Loader,
  TEXT_LENGTH,
  useIsAuthorisedHook,
  getTranslatedText,
  TooltipWrapper
} from '@nile/components';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { AccountClient, GET_FIELD } from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  Heading,
  Page,
  ResourceItem,
  TextField,
  TextStyle,
  ColorsList
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import nanoid from 'nanoid';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const BadgeWithEditStatus = ({ icon, status, onChange }) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = () => {
    setPopoverActive(!popoverActive);
  };
  const [colorData, setColorData] = useState(status);

  const onColorChange = (data) => {
    setColorData(data?.status);
    togglePopoverActive();
    if (onChange) {
      onChange(data?.status);
    }
  };

  return (
    <Tippy
      content={
        <div className="p20 p10">
          <div className="mb5">
            <TextStyle>Choose colors</TextStyle>
          </div>
          <ColorsList
            filterStatus={[
              'new',
              'secondary',
              'attention',
              'primary',
              'warningSecondary'
            ]}
            defaultStatus={colorData}
            onChange={onColorChange}
          />
        </div>
      }
      animation="scale-extreme"
      allowHTML
      visible={popoverActive}
      theme="light"
      appendTo={document.body}
      zIndex={3947483000}
      interactive
      onClickOutside={togglePopoverActive}
    >
      <div className="pointer flex actr" onClick={togglePopoverActive}>
        <Badge status={colorData || 'info'}>
          <SVGIcon name={icon || 'CustomStatus'} />
        </Badge>
      </div>
    </Tippy>
  );
};

const ICON_MAP = {
  PROSPECT: { icon: 'Prospect', status: 'info' },
  INACTIVE: { icon: 'InactiveClient', status: 'critical' },
  ACTIVE: { icon: 'ActiveClient', status: 'success' }
};

const EditMode = ({ item, onSave, onCancel, icon, disableSort, status }) => {
  const intl = useIntl();
  const [value, setValue] = useState(item?.value);

  const [data, setData] = useState(item);
  const [badgeStatus, setBadgeStatus] = useState(status);

  const focusRef = React.useRef(false);

  const onChange = (val, key) => {
    setValue(val);
    const updatedItem = cloneDeep(data);
    if (updatedItem?.[key]) {
      updatedItem[key] = val || null;
    }
    setData(updatedItem);
  };

  const save = () => {
    const trimmedValue = data?.value && data?.value?.trim();
    if (onSave && trimmedValue) {
      const formattedData = { ...data, value: trimmedValue };
      onSave(formattedData);
    }
  };

  const cancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const onBadgeColorChange = (val) => {
    setBadgeStatus(val);
    setData((prevState) => ({
      ...prevState,
      details: {
        ...prevState?.details,
        status: val
      }
    }));
  };

  const handleKeyDown = (event) => {
    if (focusRef?.current && event.key === 'Enter' && !event.shiftKey) {
      save();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <TextField
        variant="outline"
        placeholder={getTranslatedText(intl, 'common.edit.entry')}
        maxLength={TEXT_LENGTH.size_5}
        onChange={(val) => {
          onChange(val, 'value');
        }}
        value={value}
        prefix={
          <div className="flex g5 actr">
            {!disableSort && (
              <div className="flex">
                <SuperSortable.DragHandle />
              </div>
            )}
            <div className="flex actr">
              <BadgeWithEditStatus
                icon={icon}
                status={badgeStatus}
                onChange={onBadgeColorChange}
              />
            </div>
          </div>
        }
        suffix={
          <div className="flex g10 mr-5 actr">
            <TooltipWrapper title="common.save">
              <Button
                onClick={save}
                icon={<SVGIcon name="CircleTickThinOutline" />}
                monochrome
                plain
                iconColor="var(--sop-green-color)"
              />
            </TooltipWrapper>

            <TooltipWrapper title="common.cancel">
              <Button
                onClick={cancel}
                icon={<SVGIcon name="RejectedCircle" />}
                plainSecondary
                monochrome
                plain
                iconColor="var(--sops-text-critical)"
              />
            </TooltipWrapper>
          </div>
        }
        prefixBorder
        suffixBorder
        onFocus={() => {
          focusRef.current = true;
        }}
        onBlur={() => {
          focusRef.current = false;
        }}
      />
    </div>
  );
};
function Stage(props) {
  const intl = useIntl();
  const getIcon = (details) => {
    const property = details?.constants;
    const name = ICON_MAP?.[property]?.icon || 'CustomStatus';
    const status = details?.status || ICON_MAP?.[property]?.status || null;

    return { name, status };
  };

  const isEditAuthorised = useIsAuthorisedHook('clientCustomFields.edit');
  const disableSort = !isEditAuthorised;

  const { primaryAction } = props;

  const [options, setOptions] = useState(null);
  const [newType, setNewType] = useState(null);
  const [newTypeStatus, setNewTypeStatus] = useState('info');

  const [editItem, setEditItem] = useState(null);

  const createInputFocusRef = React.useRef(false);

  const onEdit = (item) => {
    setEditItem({ item, original: item });
  };

  const addOptions = () => {
    if (newType) {
      let updatedOptions = options ? cloneDeep(options) : [];
      const order = updatedOptions?.length + 1;
      const typeToAdd = {
        id: nanoid(),
        order: order,
        value: newType,
        deletable: true,
        details: {
          status: newTypeStatus
        }
      };
      updatedOptions = [...updatedOptions, typeToAdd];
      setOptions(updatedOptions);
      setNewType(null);
      setNewTypeStatus('info');
    }
  };

  const handleCreationAddOnKeyDown = (event) => {
    if (
      createInputFocusRef?.current &&
      event.key === 'Enter' &&
      !event.shiftKey
    ) {
      addOptions();
    }
  };

  const deleteItem = (item) => {
    const allOptions = cloneDeep(options);
    const updatedOptions = [];
    allOptions.forEach((el, index) => {
      if (el?.id !== item?.id) {
        updatedOptions.push({
          ...el,
          order: index + 1
        });
      }
    });
    setOptions(updatedOptions);
  };

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.client,
        columnName: DEFAULT_CLIENT_FIELDS_BY_COLUMN_NAME.STAGE
      }
    },
    onCompleted({ getField }) {
      if (getField?.options) {
        const sortedData = sortBy(getField?.options, 'order');
        setOptions(sortedData);
      }
    }
  });

  const onEditCancel = () => {
    setEditItem(null);
  };

  const onEditSave = (updatedItem) => {
    let updatedOptions = cloneDeep(options);
    updatedOptions = updatedOptions.map((el) => {
      if (el?.id === updatedItem?.id) {
        return updatedItem;
      }
      return el;
    });
    setOptions(updatedOptions);
    setEditItem(null);
  };

  const onSave = () => {
    const base = data?.getField?.options;
    const parsedData = getParsedCrudData(base, options);
    const { addedItems, deletedItems, updatedItems } = parsedData;

    const fieldOptionsInput = {
      ...(addedItems?.length && {
        added: addedItems.map(({ value, order, details }) => ({
          value,
          order,
          details
        }))
      }),
      ...(deletedItems?.length && {
        deleted: deletedItems
      }),
      ...(updatedItems?.length && {
        updated: updatedItems,
        updateOptions: updatedItems
      })
    };

    let variables = { id: data?.getField?.id };

    if (!isEmpty(fieldOptionsInput)) {
      const optionsInput = {
        ...(addedItems?.length && {
          addOptions: addedItems.map(({ value, order, details }) => ({
            value,
            order,
            details
          }))
        }),
        ...(deletedItems?.length && {
          deleteOptions: deletedItems.map(({ value, order, id }) => ({
            id
          }))
        }),
        ...(updatedItems?.length && {
          updateOptions: updatedItems.map(({ value, order, id, details }) => ({
            id,
            value,
            order,
            details
          }))
        })
      };
      variables = {
        ...variables,
        ...optionsInput
      };

      props.primaryAction(variables);
    } else {
      props?.secondaryAction();
    }
  };

  if (error) return <div>error occured </div>;

  return (
    <div className="full-height full-width">
      <div className="full-height full-width">
        <Page
          fullWidth
          variant="primary"
          contentHeight="140px"
          title={
            <Heading element="h1">
              <FormattedMessage id="attribute.stage" />
            </Heading>
          }
          subtitle={
            <FormattedMessage id="admin.client.defaultField.stage.subtitle" />
          }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={onSave}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          {loading && !options ? (
            <>
              <Loader />
            </>
          ) : (
            <>
              {isEditAuthorised && (
                <div
                  className="mb20  wm-800"
                  onKeyDown={handleCreationAddOnKeyDown}
                >
                  <TextField
                    variant="outline"
                    maxLength={TEXT_LENGTH.size_5}
                    onChange={(val) => {
                      setNewType(val);
                    }}
                    placeholder={getTranslatedText(
                      intl,
                      'common.add.new.entry'
                    )}
                    value={newType}
                    prefixBorder
                    prefix={
                      <div className="flex actr ml-5">
                        <BadgeWithEditStatus
                          icon="CustomStatus"
                          status={newTypeStatus}
                          onChange={(val) => {
                            setNewTypeStatus(val);
                          }}
                        />
                      </div>
                    }
                    suffixBorder
                    suffix={
                      <div className="flex actr mr-5">
                        <TooltipWrapper title="common.add">
                          <Button plainSecondary onClick={addOptions}>
                            <SVGIcon name="Add" />
                          </Button>
                        </TooltipWrapper>
                      </div>
                    }
                    onFocus={() => (createInputFocusRef.current = true)}
                    onBlur={() => (createInputFocusRef.current = false)}
                  />
                </div>
              )}

              <SuperSortable
                className="sort-wrapper wm-800"
                list={options || []}
                onDragEnd={(reorderedData) => {
                  const updatedDataWithOrder = reorderedData.map(
                    (field, i) => ({
                      ...field,
                      order: i + 1
                    })
                  );
                  setOptions(updatedDataWithOrder);
                }}
                animation={200}
              >
                {options &&
                  options.map((el) => {
                    const constant = el?.details?.constants;
                    const icon = getIcon(el?.details);
                    const isDeletable = el?.deletable && !constant;

                    if (el?.id === editItem?.item?.id) {
                      return (
                        <React.Fragment key={el?.id}>
                          <EditMode
                            item={el}
                            onSave={onEditSave}
                            onCancel={onEditCancel}
                            icon={icon?.name}
                            disableSort={disableSort}
                            status={icon?.status}
                          />
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={el?.id}>
                        <div className=" hiddenContainer br-5">
                          <ResourceItem secondaryHoverBgColor>
                            <div className="flex fx-sb actr">
                              <div className="flex g10 actr">
                                {isEditAuthorised && !disableSort && (
                                  <SuperSortable.DragHandle />
                                )}
                                <div>
                                  <Badge
                                    status={icon?.status || el?.details?.status}
                                  >
                                    <SVGIcon name={icon?.name} />
                                  </Badge>
                                </div>
                                <div
                                  onClick={() => {
                                    isEditAuthorised && onEdit(el);
                                  }}
                                >
                                  <TextStyle weight="SemiBold">
                                    {el?.value}
                                  </TextStyle>
                                </div>
                              </div>

                              {isEditAuthorised && (
                                <div className="hiddenActions">
                                  <div className="flex g5">
                                    <TooltipWrapper title="common.edit">
                                      <Button
                                        icon={<SVGIcon name="Edit" />}
                                        plain
                                        onClick={() => onEdit(el)}
                                      />
                                    </TooltipWrapper>

                                    {isDeletable && (
                                      <TooltipWrapper title="common.delete">
                                        <Button
                                          icon={<SVGIcon name="Delete" />}
                                          plain
                                          onClick={() => deleteItem(el)}
                                        />
                                      </TooltipWrapper>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </ResourceItem>
                        </div>
                      </React.Fragment>
                    );
                  })}
              </SuperSortable>
            </>
          )}
        </Page>
      </div>
    </div>
  );
}

export default Stage;
